import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {DataTable} from "components/dataTable/index.jsx";
import {useEffect, useState, useMemo, useCallback} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";
import ConsignmentReports from "./consignmentReports.jsx";

export default observer(function AdminBalance(props) {
  const appDataStore = useAppDataStore();
  const [activeTab, setActiveTab] = useState('manage');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBalances, setUserBalances] = useState([]);
  const [selectedBalanceType, setSelectedBalanceType] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  // Cargar usuarios al montar el componente
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await appDataStore.get("/users/list");
      setUsers(data.users || []);
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      alert("Error al cargar usuarios: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (user) => {
    try {
      setLoading(true);
      setSelectedUserId(user.id);
      setSelectedUser(user);
      setShowForm(false);
      setAmount("");
      setReason("");
      setSelectedBalanceType(null);

      // Obtener todos los tipos de saldo del usuario
      const balancesData = await appDataStore.post("/balance/user-balances", {
        userId: user.id,
      });

      setUserBalances(balancesData.balances || []);

      // Si hay balances, seleccionar el primero
      if (balancesData.balances && balancesData.balances.length > 0) {
        handleSelectBalanceType(balancesData.balances[0]);
      }
    } catch (err) {
      console.error("Error al cargar información del usuario:", err);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBalanceType = (balanceType) => {
    setSelectedBalanceType(balanceType);
    setCurrentBalance(balanceType.balance);
  };

  const handleRecharge = async (e) => {
    e.preventDefault();

    if (!selectedBalanceType) {
      alert("Debes seleccionar un tipo de saldo");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert("El monto debe ser mayor a 0");
      return;
    }

    try {
      setLoading(true);

      const res = await appDataStore.post("/balance/recharge", {
        userId: selectedUserId,
        type: selectedBalanceType.type,
        amount: parseFloat(amount),
        reason: reason || null,
      });

      alert("Saldo recargado exitosamente");
      setAmount("");
      setReason("");
      setShowForm(false);

      // Refrescar tabla
      setHistoryRefreshKey(prev => prev + 1);

      // Actualizar datos
      await handleSelectUser(selectedUser);
    } catch (err) {
      alert("Error al recargar saldo: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const historyColumns = useMemo(() => ([
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('DD/MM/YYYY HH:mm'),
      exportFormat: (row) => moment(row.createdAt).format('DD/MM/YYYY HH:mm')
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => `+${formatters.cash(cell.getValue())}`,
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'previousBalance',
      header: 'Saldo Anterior',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.previousBalance
    },
    {
      accessorKey: 'newBalance',
      header: 'Saldo Nuevo',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.newBalance
    },
    {
      accessorKey: 'reason',
      header: 'Detalles',
      enableColumnFilter: false,
      cell: ({cell}) => cell.getValue() || '-'
    },
    {
      accessorKey: 'admin',
      header: 'Realizado Por',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const admin = cell.getValue();
        return admin ? `${admin.name} (${admin.email})` : 'Sistema';
      }
    }
  ]), []);

  const historyDataFetch = useCallback(async (records, currentPage, order, search, filter) => {
    return appDataStore.post(`/app/datasource/BalanceRecharge`, {
      records,
      currentPage,
      order,
      search,
      attributes: ['id', 'amount', 'reason', 'previousBalance', 'newBalance', 'createdAt'],
      filter: [{key: 'userId', value: selectedUserId, operator: 'eq'}],
      include: [
        {
          model: 'User',
          as: 'admin',
          attributes: ['id', 'name', 'email']
        }
      ],
      searchAttributes: ['reason']
    });
  }, [appDataStore, selectedUserId]);

  const tabs = [
    {id: 'manage', label: 'Gestión de Saldos', icon: '💰'},
    {id: 'reports', label: 'Reportes de Consignación', icon: '📋'}
  ];

  return (
    <>
      <div className="p-2 text-black">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Gestión de Saldos</h2>

        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-300 mb-6 gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <span className="text-lg mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'manage' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Panel de usuarios */}
            <div
              className="lg:col-span-1 border border-gray-300 rounded-lg p-4 bg-gray-50 h-fit max-h-96 overflow-y-auto">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Usuarios</h3>
              {loading && users.length === 0 ? (
                <div className="text-sm text-gray-600">Cargando...</div>
              ) : users.length === 0 ? (
                <div className="text-sm text-gray-600">No hay usuarios</div>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                        selectedUserId === user.id
                          ? "bg-blue-500 text-white"
                          : "bg-white border border-gray-300 hover:bg-gray-100 text-gray-800"
                      }`}
                    >
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Panel de detalles del usuario */}
            <div className="lg:col-span-3">
              {selectedUser ? (
                <>
                  {/* Información del usuario y balance */}
                  <div className="border border-gray-300 rounded-lg p-6 bg-white mb-4">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          {selectedUser.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Email:</strong> {selectedUser.email}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Teléfono:</strong> {selectedUser.phone || "-"}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="text-sm text-gray-600 mb-2">Saldo Actual</p>
                        <p className="text-4xl font-bold text-green-600">
                          {formatters.cash(currentBalance)}
                        </p>
                      </div>
                    </div>

                    {/* Selección de tipo de saldo */}
                    {userBalances.length > 0 && (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tipo de Saldo
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {userBalances.map((balance) => (
                            <button
                              key={balance.id}
                              onClick={() => handleSelectBalanceType(balance)}
                              className={`px-4 py-2 rounded font-medium transition-colors ${
                                selectedBalanceType?.id === balance.id
                                  ? "bg-blue-600 text-white"
                                  : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                              }`}
                            >
                              <div className="text-sm">{balance.type}</div>
                              <div className="text-xs">
                                {formatters.cash(balance.balance)}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Botón para recargar */}
                    {!showForm && (
                      <button
                        onClick={() => setShowForm(true)}
                        disabled={loading || !selectedBalanceType}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-medium"
                      >
                        Recargar Saldo
                      </button>
                    )}
                  </div>

                  {/* Formulario de recarga */}
                  {showForm && (
                    <div className="border border-blue-300 rounded-lg p-6 bg-blue-50 mb-4">
                      <h4 className="text-lg font-semibold mb-4 text-gray-800">
                        Nueva Recarga
                      </h4>
                      {selectedBalanceType && (
                        <div className="bg-white border border-blue-200 rounded p-3 mb-4">
                          <p className="text-sm text-gray-700">
                            <strong>Tipo de Saldo:</strong> {selectedBalanceType.type}
                          </p>
                          <p className="text-sm text-gray-700">
                            <strong>Saldo Actual:</strong> {formatters.cash(selectedBalanceType.balance)}
                          </p>
                        </div>
                      )}
                      <form onSubmit={handleRecharge} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Monto ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Detalles (opcional)
                          </label>
                          <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Ej: Pago mensual, promoción, etc."
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={loading || !amount}
                            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium"
                          >
                            Confirmar Recarga
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowForm(false);
                              setAmount("");
                              setReason("");
                            }}
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded font-medium"
                          >
                            Cancelar
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Historial de recargas */}
                  <div className="border border-gray-300 rounded-lg p-6 bg-white">
                    <h4 className="text-lg font-semibold mb-4 text-gray-800">
                      Historial de Recargas
                    </h4>
                    <DataTable
                      exportFileName={`historial_recargas_${selectedUser.name}_${moment().format('DDMMYY_HHmm')}`}
                      columns={historyColumns}
                      dataFetch={historyDataFetch}
                      defaultSorting={[{id: 'createdAt', desc: true}]}
                      refreshDependencies={historyRefreshKey}
                    />
                  </div>
                </>
              ) : (
                <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 text-center text-gray-600">
                  Selecciona un usuario de la lista para ver su balance e historial
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab Content - Reports */}
        {activeTab === 'reports' && (
          <div>
            <ConsignmentReports {...props} />
          </div>
        )}
      </div>
    </>
  );
});
